import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, Select, InputNumber, message, Space, Row, Col, Grid, Typography, Card, Collapse } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { soDiBuongService, SoDiBuongListItem, SoDiBuongDto, SaveSoDiBuongRequest, SoDiBuongDanhGia } from '../services/soDiBuongService';
import { masterDataService, Employee, Department } from '../services/masterDataService';
import { authService } from '../services/authService';
import { User } from '../services/userService';
import SoDiBuongReport from './SoDiBuongReport';
import dayjs, { Dayjs } from 'dayjs';

const { Option } = Select;
const { useBreakpoint } = Grid;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Helper to check permissions (copied from App.tsx)
const hasPermission = (permission: string) => {
    const storedPermissions = localStorage.getItem('permissions');
    if (!storedPermissions) return false;
    try {
        const permissionsList: string[] = JSON.parse(storedPermissions);
        return permissionsList.includes(permission);
    } catch (e) {
        console.error("Failed to parse permissions from localStorage", e);
        return false;
    }
};

interface SoDiBuongProps {
    loai?: number;
}

const HIDDEN_FIELDS_FOR_LOAI_2 = [
    'tongbn', 'sobnc1', 'sobnc2', 'sobnc3', 
    'ghitenthuoc', 'kimluu', 'ghihs', 
    'tuvanvv', 'tuvanrv', 
    'ktchamsoc', 'xulydungcu', 
    'thannhanbn', 'dongphucbn', 
    'changagoi', 'oxy'
];

const SoDiBuong: React.FC<SoDiBuongProps> = ({ loai = 1 }) => {
    const [soDiBuongs, setSoDiBuongs] = useState<SoDiBuongListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState<SoDiBuongDto | null>(null);
    const [viewingRecord, setViewingRecord] = useState<SoDiBuongDto | null>(null);
    const [form] = Form.useForm();

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [danhGias, setDanhGias] = useState<SoDiBuongDanhGia[]>([]);
    
    const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs());
    const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [filterKhoa, setFilterKhoa] = useState<number | 'all'>('all');

    const screens = useBreakpoint();

    useEffect(() => {
        fetchMasterData();
        authService.getProfile().then(setCurrentUser).catch(() => {
            message.error('Lỗi khi tải thông tin người dùng');
        });
    }, []);

    useEffect(() => {
        if (currentUser && departments.length > 0) {
            const canViewAll = hasPermission('Tatcakhoa');
            if (!canViewAll) {
                const userDept = departments.find(d => d.tenkk === currentUser.departmentName);
                if (userDept) {
                    setFilterKhoa(userDept.makk);
                } else {
                    // Handle case where user's department is not in the list
                    setFilterKhoa(0); // Or some other default/error value
                }
            } else {
                setFilterKhoa('all');
            }
        }
    }, [currentUser, departments]);

    useEffect(() => {
        // Run search only after currentUser is set, to ensure permission check is valid
        if (currentUser) {
            handleSearch();
        }
    }, [fromDate, toDate, filterKhoa]); // Re-run search if date or filter changes



    const fetchMasterData = async () => {
        try {
            const [empRes, deptRes, danhGiaRes] = await Promise.all([
                masterDataService.getEmployees(),
                masterDataService.getDepartments(),
                soDiBuongService.getAllDanhGia(),
            ]);
            setEmployees(empRes.data);
            setDepartments(deptRes.data);
            setDanhGias(danhGiaRes.data);
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu gốc');
        }
    };

    const isVisible = (fieldName: string) => {
        if (loai === 2 && HIDDEN_FIELDS_FOR_LOAI_2.includes(fieldName)) {
            return false;
        }
        return true;
    };

    const handleSearch = async () => {
        if (!fromDate || !toDate) {
            message.error('Vui lòng chọn ngày bắt đầu và kết thúc');
            return;
        }
        setLoading(true);
        try {
            const khoaId = filterKhoa === 'all' ? 0 : filterKhoa;
            const response = await soDiBuongService.getAll(fromDate.format('YYYY-MM-DD'), toDate.format('YYYY-MM-DD'), khoaId, loai);
            setSoDiBuongs(response.data);
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({ ngay: dayjs(), loai: loai });
        setIsModalVisible(true);
    };

    const handleEdit = async (id: number) => {
        try {
            const response = await soDiBuongService.getById(id);
            const record = response.data;
            const multiSelectFields = ['manvkt', 'maddnghiphep', 'maddctac', 'maddnghisinh', 'maddnghikl', 'maddtruc', 'maddnghitruc'];
            const processedRecord: { [key: string]: any } = { ...record };

            multiSelectFields.forEach(field => {
                const value = record[field as keyof SoDiBuongDto];
                if (typeof value === 'string' && value) {
                    processedRecord[field] = value.split(',');
                } else {
                    processedRecord[field] = [];
                }
            });

            form.setFieldsValue({ 
                ...processedRecord, 
                ngay: dayjs(record.ngay),
                soddtangcuong: record.soddtangcuong ? record.soddtangcuong : 0
            });
            setIsModalVisible(true);
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu phiếu');
        }
    };

    const handleView = async (id: number) => {
        try {
            const response = await soDiBuongService.getById(id);
            setViewingRecord(response.data);
            setIsViewModalVisible(true);
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu xem phiếu');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await soDiBuongService.delete(id);
            message.success('Xóa phiếu thành công');
            handleSearch();
        } catch (error) {
            message.error('Lỗi khi xóa phiếu');
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            
            const multiSelectFields = ['manvkt', 'maddnghiphep', 'maddctac', 'maddnghisinh', 'maddnghikl', 'maddtruc', 'maddnghitruc'];
            const processedValues = { ...values };

            multiSelectFields.forEach(field => {
                if (Array.isArray(values[field])) {
                    processedValues[field] = values[field].join(',');
                }
            });

            const { maphieu, ...restOfValues } = processedValues;

            const request: SaveSoDiBuongRequest = { 
                ...restOfValues, 
                ngay: values.ngay.format('YYYY-MM-DD'),
                soddtangcuong: parseFloat(values.soddtangcuong),
                loai: loai
            };

            if (maphieu) {
                await soDiBuongService.update(maphieu, request);
                message.success('Cập nhật phiếu thành công');
            } else {
                await soDiBuongService.create(request);
                message.success('Tạo phiếu thành công');
            }
            setIsModalVisible(false);
            setEditingRecord(null);
            handleSearch();
        } catch (error) {
            message.error('Lỗi khi lưu phiếu');
        }
    };

    const getDanhGiaText = (id?: number) => {
        return danhGias.find(d => d.iddanhgia === id)?.tendanhgia || 'N/A';
    };

    const getEmployeeNames = (idString?: string) => {
        if (!idString) return '-';
        const ids = idString.split(',');
        return ids.map(id => {
            const emp = employees.find(e => e.macc === id);
            return emp ? emp.tennv : id;
        }).join(', ');
    };

    const columns: ColumnType<SoDiBuongListItem>[] = [
        { title: 'Mã phiếu', dataIndex: 'maphieu', key: 'maphieu', fixed: 'left', width: 100 },
        { title: 'Ngày', dataIndex: 'ngay', key: 'ngay', render: (text: string) => dayjs(text).format('DD/MM/YYYY'), width: 120 },
        { title: 'Khoa', dataIndex: 'tenKhoa', key: 'tenKhoa', width: 200 },
        { title: 'Người kiểm tra', dataIndex: 'tenNvkt', key: 'tenNvkt', width: 200 },
        {
            title: 'Hành động',
            key: 'action',
            fixed: 'right',
            width: 180,
            render: (_: any, record: SoDiBuongListItem) => (
                <Space size="middle">
                    <Button onClick={() => handleView(record.maphieu)}>Xem</Button>
                    <Button onClick={() => handleEdit(record.maphieu)}>Sửa</Button>
                    <Button danger onClick={() => handleDelete(record.maphieu)}>Xóa</Button>
                </Space>
            ),
        },
    ];

    const renderEmployeeOptions = () => employees.map(emp => <Option key={emp.macc} value={emp.macc}>{emp.tennv} ({emp.macc})</Option>);
    const renderDepartmentOptions = () => departments.map(dept => <Option key={dept.makk} value={dept.makk}>{dept.tenkk}</Option>);
    const renderDanhGiaOptions = () => danhGias.map(dg => <Option key={dg.iddanhgia} value={dg.iddanhgia}>{dg.tendanhgia}</Option>);

    const renderViewSection = (title: string, items: { label: string, value: any, key?: string }[]) => (
        <Card title={title} size="small" style={{ marginBottom: 16 }}>
            {items.map((item, index) => (
                <Row key={index} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: index === items.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
                    <Col span={10}><strong>{item.label}</strong></Col>
                    <Col span={14}>{item.value || '-'}</Col>
                </Row>
            ))}
        </Card>
    );

    const renderViewModalContent = () => {
        if (!viewingRecord) return null;
        
        const filterItems = (items: { label: string, value: any, key: string }[]) => 
            items.filter(item => isVisible(item.key));

        return (
            <>
                {renderViewSection('I. HÀNH CHÍNH', [
                    { label: 'Ngày đi buồng', value: dayjs(viewingRecord.ngay).format('DD/MM/YYYY'), key: 'ngay' },
                    { label: 'Người kiểm tra', value: getEmployeeNames(viewingRecord.manvkt), key: 'manvkt' },
                    { label: 'Khoa', value: viewingRecord.tenKhoa, key: 'makhoa' },
                ])}
                {renderViewSection('II. TÌNH HÌNH NGƯỜI BỆNH', filterItems([
                    { label: 'Tổng số bệnh nhân', value: viewingRecord.tongbn, key: 'tongbn' },
                    { label: 'Số bệnh nhân chăm sóc cấp 1', value: viewingRecord.sobnc1, key: 'sobnc1' },
                    { label: 'Số bệnh nhân chăm sóc cấp 2', value: viewingRecord.sobnc2, key: 'sobnc2' },
                    { label: 'Số bệnh nhân chăm sóc cấp 3', value: viewingRecord.sobnc3, key: 'sobnc3' },
                ]))}
                {renderViewSection('III. TÌNH HÌNH NHÂN LỰC', filterItems([
                    { label: 'Tình hình nhân lực', value: viewingRecord.thnhanluc, key: 'thnhanluc' },
                    { label: 'Số Bác sĩ đi làm', value: viewingRecord.bsdilam, key: 'bsdilam' },
                    { label: 'Tổng số điều dưỡng (KTV, HS)', value: viewingRecord.tongddktv, key: 'tongddktv' },
                    { label: 'Điều dưỡng nghỉ trực', value: getEmployeeNames(viewingRecord.maddnghitruc), key: 'maddnghitruc' },
                    { label: 'Điều dưỡng Nghỉ phép - bù', value: getEmployeeNames(viewingRecord.maddnghiphep), key: 'maddnghiphep' },
                    { label: 'Điều dưỡng công tác – đi học', value: getEmployeeNames(viewingRecord.maddctac), key: 'maddctac' },
                    { label: 'Điều dưỡng nghỉ sinh', value: getEmployeeNames(viewingRecord.maddnghisinh), key: 'maddnghisinh' },
                    { label: 'Điều dưỡng nghỉ không lương', value: getEmployeeNames(viewingRecord.maddnghikl), key: 'maddnghikl' },
                    { label: 'Số Điều dưỡng tăng cường', value: viewingRecord.soddtangcuong, key: 'soddtangcuong' },
                    { label: 'Số Điều dưỡng đi làm', value: viewingRecord.sodddilam, key: 'sodddilam' },
                    { label: 'Điều dưỡng trực', value: getEmployeeNames(viewingRecord.maddtruc), key: 'maddtruc' },
                ]))}
                {renderViewSection('IV. CÔNG TÁC CHUYÊN MÔN', filterItems([
                    { label: 'Thực hiện y lệnh đầy đủ, chính xác, an toàn, đúng giờ', value: getDanhGiaText(viewingRecord.thyl), key: 'thyl' },
                    { label: 'Cấp thuốc theo y lệnh cho người bệnh', value: getDanhGiaText(viewingRecord.capthuoc), key: 'capthuoc' },
                    { label: 'Công khai thuốc đủ, đúng rõ ràng, có chữ ký của người bệnh', value: getDanhGiaText(viewingRecord.congkhaithuoc), key: 'congkhaithuoc' },
                    { label: 'Kê thanh toán chi phí khám, chữa bệnh đủ đúng, kịp thời', value: getDanhGiaText(viewingRecord.kettcp), key: 'kettcp' },
                    { label: 'Ghi tên thuốc rõ ràng trên chai dịch truyền khi có chỉ định pha thuốc vào dịch truyền', value: getDanhGiaText(viewingRecord.ghitenthuoc), key: 'ghitenthuoc' },
                    { label: 'Kim lưu phải ghi ngày, giờ, tên ĐD thực hiện', value: getDanhGiaText(viewingRecord.kimluu), key: 'kimluu' },
                    { label: 'Xe tiêm, thay băng đúng quy định có hộp thuốc chống sốc đầy đủ theo quy định', value: getDanhGiaText(viewingRecord.xetiem), key: 'xetiem' },
                    { label: 'Ghi hồ sơ điều dưỡng đầy đủ, phù hợp diễn tiến bệnh. Đối với BNCS cấp 1, 2, 3, lập kế hoạch chăm sóc đầy đủ', value: getDanhGiaText(viewingRecord.ghihs), key: 'ghihs' },
                ]))}
                {renderViewSection('V. CÔNG TÁC TƯ VẤN, GDSK', filterItems([
                    { label: 'Hướng dẫn NB/thân nhân cách chăm sóc, vệ sinh cá nhân, chế độ dinh dưỡng, dùng thuốc', value: getDanhGiaText(viewingRecord.huongdan), key: 'huongdan' },
                    { label: 'Phối hợp, hỗ trợ đồng nghiệp', value: getDanhGiaText(viewingRecord.phoihopdn), key: 'phoihopdn' },
                    { label: 'Giao tiếp tốt với NB/người nuôi bệnh, đồng nghiệp', value: getDanhGiaText(viewingRecord.giaotiep), key: 'giaotiep' },
                    { label: 'Người bệnh (NN) được tư vấn, giáo dục sức khỏe phù hợp với bệnh khi vào viện, trong quá trình điều trị', value: viewingRecord.tuvanvv, key: 'tuvanvv' },
                    { label: 'Người bệnh (NN) được tư vấn, giáo dục sức khỏe phù hợp trước khi ra viện', value: viewingRecord.tuvanrv, key: 'tuvanrv' },
                    { label: 'Người bệnh có được các kiến thức, thực hành thiết yếu để tự theo dõi, chăm sóc', value: viewingRecord.tutheodoi, key: 'tutheodoi' },
                ]))}
                {renderViewSection('VI. CÔNG TÁC KIỂM SOÁT NHIỄM KHUẨN', filterItems([
                    { label: 'Rửa tay thường quy', value: getDanhGiaText(viewingRecord.rttq), key: 'rttq' },
                    { label: 'Thực hiện kỹ thuật chăm sóc đúng quy trình', value: getDanhGiaText(viewingRecord.ktchamsoc), key: 'ktchamsoc' },
                    { label: 'Xử lý dụng cụ, bình làm ấm oxy, hút đàm', value: getDanhGiaText(viewingRecord.xulydungcu), key: 'xulydungcu' },
                    { label: 'Phân loại và xử lý rác thải', value: getDanhGiaText(viewingRecord.plrac), key: 'plrac' },
                    { label: 'Khu vực hành lang, buồng bệnh, phòng nhân viên sạch đảm bảo 5S', value: getDanhGiaText(viewingRecord.bb5s), key: 'bb5s' },
                ]))}
                {renderViewSection('VII. AN TOÀN NGƯỜI BỆNH', filterItems([
                    { label: 'Thân nhân BN tuân thủ nội quy buồng, phòng bệnh', value: getDanhGiaText(viewingRecord.thannhanbn), key: 'thannhanbn' },
                    { label: 'Người bệnh mặc đồng phục bệnh viện đầy đủ', value: getDanhGiaText(viewingRecord.dongphucbn), key: 'dongphucbn' },
                ]))}
                {renderViewSection('VIII. CƠ SỞ VẬT CHẤT, TRANG THIẾT BỊ', filterItems([
                    { label: 'Các trang thiết bị, máy móc hoạt động tốt', value: getDanhGiaText(viewingRecord.ttb), key: 'ttb' },
                    { label: 'Quần, áo, chăn, gối, ga giường cung cấp đầy đủ, sạch sẽ', value: getDanhGiaText(viewingRecord.changagoi), key: 'changagoi' },
                    { label: 'Hệ thống Oxy, hút đàm hoạt động tốt', value: getDanhGiaText(viewingRecord.oxy), key: 'oxy' },
                    { label: 'Tủ lạnh bảo quản thuốc, vắc xin đúng quy định', value: getDanhGiaText(viewingRecord.tulanh), key: 'tulanh' },
                    { label: 'Các vật tư, cơ sở vật chất, máy móc hư hỏng được đề xuất sửa chữa kịp thời', value: getDanhGiaText(viewingRecord.suachuacsvc), key: 'suachuacsvc' },
                    { label: 'Sử dụng tiết kiệm: điện, nước, các vật tư y tế, văn phòng phẩm', value: getDanhGiaText(viewingRecord.tietkiem), key: 'tietkiem' },
                ]))}
                {renderViewSection('IX. NHỮNG LƯU Ý SAU KHI ĐI BUỒNG', [
                    { label: 'NHỮNG LƯU Ý SAU KHI ĐI BUỒNG', value: viewingRecord.luuy, key: 'luuy' },
                ])}
            </>
        );
    };

    const renderForm = () => (
        <Form form={form} layout="vertical" initialValues={{ sobnc1: 0, sobnc2: 0, sobnc3: 0, tongbn: 0, thnhanluc: 0, bsdilam: 0, tongddktv: 0, sodddilam: 0, soddtangcuong: 0, tuvanvv: 0, tuvanrv: 0, tutheodoi: 0 }}>
            <Form.Item name="maphieu" hidden><Input /></Form.Item>
            <Collapse defaultActiveKey={['1']} ghost>
                <Panel header="I. HÀNH CHÍNH" key="1">
                    <Row gutter={16}>
                        <Col xs={24} md={8}><Form.Item name="ngay" label="Ngày đi buồng" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="manvkt" label="Người kiểm tra" rules={[{ required: true }]}><Select showSearch mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={24} md={8}><Form.Item name="makhoa" label="Khoa" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{renderDepartmentOptions()}</Select></Form.Item></Col>
                    </Row>
                </Panel>
                <Panel header="II. TÌNH HÌNH NGƯỜI BỆNH" key="2">
                    <Row gutter={16}>
                        {isVisible('tongbn') && <Col xs={12} md={6}><Form.Item name="tongbn" label="Tổng số bệnh nhân"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                        {isVisible('sobnc1') && <Col xs={12} md={6}><Form.Item name="sobnc1" label="Số bệnh nhân chăm sóc cấp 1"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                        {isVisible('sobnc2') && <Col xs={12} md={6}><Form.Item name="sobnc2" label="Số bệnh nhân chăm sóc cấp 2"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                        {isVisible('sobnc3') && <Col xs={12} md={6}><Form.Item name="sobnc3" label="Số bệnh nhân chăm sóc cấp 3"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="III. TÌNH HÌNH NHÂN LỰC" key="3">
                    <Row gutter={16}>
                        <Col xs={12} md={6}><Form.Item name="thnhanluc" label="Tình hình nhân lực"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item name="bsdilam" label="Số Bác sĩ đi làm"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item name="tongddktv" label="Tổng số điều dưỡng (KTV, HS)"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddnghitruc" label="Điều dưỡng nghỉ trực"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddnghiphep" label="Điều dưỡng Nghỉ phép - bù"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddctac" label="Điều dưỡng công tác – đi học"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddnghisinh" label="Điều dưỡng nghỉ sinh"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddnghikl" label="Điều dưỡng nghỉ không lương"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item name="soddtangcuong" label="Số Điều dưỡng tăng cường"><InputNumber style={{ width: '100%' }} precision={1} /></Form.Item></Col>
                        <Col xs={12} md={6}><Form.Item name="sodddilam" label="Số Điều dưỡng đi làm"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
                        <Col xs={24} md={6}><Form.Item name="maddtruc" label="Điều dưỡng trực"><Select showSearch allowClear mode="multiple" optionFilterProp="children">{renderEmployeeOptions()}</Select></Form.Item></Col>
                    </Row>
                </Panel>
                <Panel header="IV. CÔNG TÁC CHUYÊN MÔN" key="4">
                    <Row gutter={16}>
                        {isVisible('thyl') && <Col xs={24} md={12}><Form.Item name="thyl" label="Thực hiện y lệnh đầy đủ, chính xác, an toàn, đúng giờ"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('capthuoc') && <Col xs={24} md={12}><Form.Item name="capthuoc" label="Cấp thuốc theo y lệnh cho người bệnh"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('congkhaithuoc') && <Col xs={24} md={12}><Form.Item name="congkhaithuoc" label="Công khai thuốc đủ, đúng rõ ràng, có chữ ký của người bệnh"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('kettcp') && <Col xs={24} md={12}><Form.Item name="kettcp" label="Kê thanh toán chi phí khám, chữa bệnh đủ đúng, kịp thời"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('ghitenthuoc') && <Col xs={24} md={12}><Form.Item name="ghitenthuoc" label="Ghi tên thuốc rõ ràng trên chai dịch truyền khi có chỉ định pha thuốc vào dịch truyền"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('kimluu') && <Col xs={24} md={12}><Form.Item name="kimluu" label="Kim lưu phải ghi ngày, giờ, tên ĐD thực hiện"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('xetiem') && <Col xs={24} md={12}><Form.Item name="xetiem" label="Xe tiêm, thay băng đúng quy định có hộp thuốc chống sốc đầy đủ theo quy định"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('ghihs') && <Col xs={24} md={12}><Form.Item name="ghihs" label="Ghi hồ sơ điều dưỡng đầy đủ, phù hợp diễn tiến bệnh. Đối với BNCS cấp 1, 2, 3, lập kế hoạch chăm sóc đầy đủ"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="V. CÔNG TÁC TƯ VẤN, GDSK" key="5">
                     <Row gutter={16}>
                        {isVisible('huongdan') && <Col xs={24} md={12}><Form.Item name="huongdan" label="Hướng dẫn NB/thân nhân cách chăm sóc, vệ sinh cá nhân, chế độ dinh dưỡng, dùng thuốc"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('phoihopdn') && <Col xs={24} md={12}><Form.Item name="phoihopdn" label="Phối hợp, hỗ trợ đồng nghiệp"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('giaotiep') && <Col xs={24} md={12}><Form.Item name="giaotiep" label="Giao tiếp tốt với NB/người nuôi bệnh, đồng nghiệp"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('tuvanvv') && <Col xs={24} md={12}><Form.Item name="tuvanvv" label="Người bệnh (NN) được tư vấn, giáo dục sức khỏe phù hợp với bệnh khi vào viện, trong quá trình điều trị"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                        {isVisible('tuvanrv') && <Col xs={24} md={12}><Form.Item name="tuvanrv" label="Người bệnh (NN) được tư vấn, giáo dục sức khỏe phù hợp trước khi ra viện"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                        {isVisible('tutheodoi') && <Col xs={24} md={12}><Form.Item name="tutheodoi" label="Người bệnh có được các kiến thức, thực hành thiết yếu để tự theo dõi, chăm sóc"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="VI. CÔNG TÁC KIỂM SOÁT NHIỄM KHUẨN" key="6">
                    <Row gutter={16}>
                        {isVisible('rttq') && <Col xs={24} md={8}><Form.Item name="rttq" label="Rửa tay thường quy"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('ktchamsoc') && <Col xs={24} md={8}><Form.Item name="ktchamsoc" label="Thực hiện kỹ thuật chăm sóc đúng quy trình"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('xulydungcu') && <Col xs={24} md={8}><Form.Item name="xulydungcu" label="Xử lý dụng cụ, bình làm ấm oxy, hút đàm"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('plrac') && <Col xs={24} md={8}><Form.Item name="plrac" label="Phân loại và xử lý rác thải"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('bb5s') && <Col xs={24} md={8}><Form.Item name="bb5s" label="Khu vực hành lang, buồng bệnh, phòng nhân viên sạch đảm bảo 5S"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="VII. AN TOÀN NGƯỜI BỆNH" key="7">
                    <Row gutter={16}>
                        {isVisible('thannhanbn') && <Col xs={24} md={12}><Form.Item name="thannhanbn" label="Thân nhân BN tuân thủ nội quy buồng, phòng bệnh"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('dongphucbn') && <Col xs={24} md={12}><Form.Item name="dongphucbn" label="Người bệnh mặc đồng phục bệnh viện đầy đủ"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="VIII. CƠ SỞ VẬT CHẤT, TRANG THIẾT BỊ" key="8">
                    <Row gutter={16}>
                        {isVisible('ttb') && <Col xs={24} md={8}><Form.Item name="ttb" label="Các trang thiết bị, máy móc hoạt động tốt"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('changagoi') && <Col xs={24} md={8}><Form.Item name="changagoi" label="Quần, áo, chăn, gối, ga giường cung cấp đầy đủ, sạch sẽ"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('oxy') && <Col xs={24} md={8}><Form.Item name="oxy" label="Hệ thống Oxy, hút đàm hoạt động tốt"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('tulanh') && <Col xs={24} md={8}><Form.Item name="tulanh" label="Tủ lạnh bảo quản thuốc, vắc xin đúng quy định"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('suachuacsvc') && <Col xs={24} md={8}><Form.Item name="suachuacsvc" label="Các vật tư, cơ sở vật chất, máy móc hư hỏng được đề xuất sửa chữa kịp thời"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                        {isVisible('tietkiem') && <Col xs={24} md={8}><Form.Item name="tietkiem" label="Sử dụng tiết kiệm: điện, nước, các vật tư y tế, văn phòng phẩm"><Select>{renderDanhGiaOptions()}</Select></Form.Item></Col>}
                    </Row>
                </Panel>
                <Panel header="IX. NHỮNG LƯU Ý SAU KHI ĐI BUỒNG" key="9">
                    <Form.Item name="luuy" label="NHỮNG LƯU Ý SAU KHI ĐI BUỒNG"><Input.TextArea rows={4} /></Form.Item>
                </Panel>
            </Collapse>
        </Form>
    );

    const renderMobileList = () => (
        <div style={{ paddingTop: 16 }}>
            {soDiBuongs.map(item => (
                <Card 
                    key={item.maphieu} 
                    title={`Mã phiếu: ${item.maphieu}`} 
                    style={{ marginBottom: 16 }}
                    extra={<Text type="secondary">{dayjs(item.ngay).format('DD/MM/YYYY')}</Text>}
                >
                    <p><strong>Khoa:</strong> {item.tenKhoa}</p>
                    <p><strong>Người kiểm tra:</strong> {item.tenNvkt}</p>
                    <Space style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={() => handleView(item.maphieu)}>Xem</Button>
                        <Button onClick={() => handleEdit(item.maphieu)}>Sửa</Button>
                        <Button danger onClick={() => handleDelete(item.maphieu)}>Xóa</Button>
                    </Space>
                </Card>
            ))}
        </div>
    );

    return (
        <div style={{ padding: '24px' }}>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <DatePicker value={fromDate} onChange={setFromDate} />
                <DatePicker value={toDate} onChange={setToDate} />
                <Select
                    value={filterKhoa}
                    onChange={setFilterKhoa}
                    style={{ width: 200 }}
                    disabled={!hasPermission('Tatcakhoa')}
                >
                    {hasPermission('Tatcakhoa') ? 
                        [<Option key="all" value="all">Tất cả</Option>].concat(
                            departments.map(dept => <Option key={dept.makk} value={dept.makk}>{dept.tenkk}</Option>)
                        ) : (
                            currentUser && departments.find(d => d.tenkk === currentUser.departmentName) &&
                            <Option key={departments.find(d => d.tenkk === currentUser.departmentName)!.makk} value={departments.find(d => d.tenkk === currentUser.departmentName)!.makk}>
                                {currentUser.departmentName}
                            </Option>
                        )
                    }
                </Select>
                <Button type="primary" onClick={handleSearch}>Tìm kiếm</Button>
                <Button onClick={handleAdd} type="primary">
                    Thêm phiếu
                </Button>
            </Space>
            {screens.md ? (
                <Table columns={columns} dataSource={soDiBuongs} loading={loading} rowKey="maphieu" scroll={{ x: 800 }} />
            ) : (
                renderMobileList()
            )}
            {hasPermission('BcSodibuong') && <SoDiBuongReport loai={loai} />}
            <Modal title={editingRecord ? 'Sửa phiếu' : 'Thêm phiếu'} open={isModalVisible} onOk={handleOk} onCancel={() => setIsModalVisible(false)} width={screens.lg ? '80%' : '95%'} bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {renderForm()}
            </Modal>
            <Modal title={`CHI TIẾT SỔ ĐI BUỒNG - KHOA ${viewingRecord?.tenKhoa?.toUpperCase() || ''}`} open={isViewModalVisible} onCancel={() => setIsViewModalVisible(false)} footer={null} width={screens.lg ? '80%' : '95%'} bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {renderViewModalContent()}
            </Modal>
        </div>
    );
};

export default SoDiBuong;